<template>
  <div
    v-if="pageLoad && !this.loading"
    class="blog-post">
    <img :src="backgroundTop" class="academy__pennellata-top">
    <div class="blog-post__content-container">
      <h1 class="blog-post__content-container__title">{{ currentPost.fields.title }}</h1>
      <!-- youtube div element -->
      <div class="blog-post__content-container__content">
        <div v-if="typeof currentPost.fields.videoId !== 'undefined'">
          <vue-plyr>
            <div data-plyr-provider="youtube" :data-plyr-embed-id="currentPost.fields.videoId"></div>
          </vue-plyr>
        </div>
        <div 
          v-else
          class="blog-post__content-container__image">
          <img
            :src="currentPost.fields.image.fields.file.url"
            :alt="currentPost.fields.image.fields.description"
            :width="currentPost.fields.image.fields.file.details.image.width"
            :height="currentPost.fields.image.fields.file.details.image.height">
        </div>
        <div v-html="description"></div>
      </div>
    </div>
    <SharePage :sharedUrl="currentUrl" :pageTitle="pageTitle" />

    <div 
      v-if="currentPost.fields.products.length"
      class="product-same-category">
      <div class="site-paddings">
        <div class="title--section__title-area">
          <h3>In questo articolo</h3>
        </div>
      </div>
      <SwiperTab :list="currentPost.fields.products" />
    </div>

    <StayUpToDate />
    <swiperCosaUsare
      :overlineTitle="currentPost.fields.category.fields.labelOthersLike"
      :category="currentPost.fields.category.fields.title"
      :currentSlug="currentPost.fields.slug"/>
    <FooterArea/>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types'
import { documentToHtmlString } from '@contentful/rich-text-html-renderer'

const options = {
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: (node, next) => {
      return `<div class="blog-post__content-container__image"><img src="${node.data.target.fields.file.url}" alt="" width="${node.data.target.fields.file.details.image.width}" height="${node.data.target.fields.file.details.image.height}" alt="${node.data.target.fields.title}"></div>`
    },
    [INLINES.ENTRY_HYPERLINK]: (node, next) => {
      const contentTypeId = node.data.target.sys.contentType.sys.id
      const map = {
        'products': 'prodotto',
        'category': 'categoria',
        'posts': 'cosa-usare-per',
        'tags': 'tag'
      }
      if (typeof map[contentTypeId] !== 'undefined'){
        return `<a class="underline_link" target="_blank" href="/${map[contentTypeId]}/${node.data.target.fields.slug}/">${node.content[0].value}</a>`
      }
      else return '<strong>Problem during link render</strong>'
    },
    [INLINES.HYPERLINK]: (node, next) => {
      return `<a class="underline_link" target="_blank" href="${node.data.uri}">${node.content[0].value}</a>`
    }
  },
  renderMark: {
    // Example
    [MARKS.BOLD]: text => `<strong>${text}</strong>`
    // [MARKS.CODE]
  }
}

import SwiperTab from '@/components/SwiperTab/SwiperTab.vue'
import FooterArea from '@/components/FooterArea/FooterArea.vue'
import StayUpToDate from '@/components/StayUpToDate/StayUpToDate.vue'
import SharePage from '@/components/SharePage/SharePage.vue'
import SwiperCosaUsare from '@/components/SwiperCosaUsare/SwiperCosaUsare.vue'
import getSinglePages from '@/mixins/getSinglePages.js'

export default {
  name: 'BlogPost',
  mixins: [getSinglePages],
  components: {
    FooterArea,
    StayUpToDate,
    SharePage,
    SwiperCosaUsare,
    SwiperTab
  },
  props: {
    slug: {
      type: String,
      required: true
    }
  },
  data: () => ({
    loading: true,
    backgroundTop: require('@/assets/academy/pennellata-top.jpg')
  }),
  computed: {
    ...mapGetters('contentful', [
      'blogPost'
    ]),
    pageLoad: {
      get () {
        return typeof this.blogPost(this.slug) !== 'undefined'
      }
    },
    currentPost: {
      get () {
        return this.blogPost(this.slug)
      }
    },
    description: {
      get () {
        return !this.loading ? documentToHtmlString(this.currentPost.fields.richContent, options) : ''
      }
    },
    currentUrl: {
      get () {
        return 'http://www.colorificiopostumia.it' + this.$route.fullPath
      }
    },
    pageTitle: {
      get () {
        return !this.loading ? this.currentPost.fields.title : ''
      }
    }
  },
  beforeRouteEnter (from, to, next) {
    next(async (vm) => {
	    await vm.loadData()
      if (typeof vm.currentPost === 'undefined') vm.$router.replace({ name: 'not-found-page' })
    })
  },
  beforeRouteUpdate (from, to, next) {
    this.loadData()
    next()
  },
  methods: {
    ...mapActions('contentful', [
      'loadBlogPosts'
    ]),
    async loadData () {
      this.loading = true
      try {
        await this.loadBlogPosts()
        this.loading = false
      } catch (e) {
        // TODO sentry
      }
    }
  },
  metaInfo () {
    if (this.pageLoad && !this.loading) return {
      title: this.currentPost.fields.seoTitle.length >= 40 ? `${this.currentPost.fields.seoTitle} | Colorificio Postumia` : `${this.currentPost.fields.title} | Colorificio Postumia`,
      meta: [
        { vmid: 'description', name: 'description', content: this.currentPost.fields.seoDescription.length >= 100 ? this.currentPost.fields.seoDescription : `Non sai come ${this.currentPost.fields.title}? Inizia con i prodotti giusti! Gli esperti del Colorificio Postumia ti spiegano quali prodotti usare per ${this.currentPost.fields.title}` },
        { property: 'og:locale', content: 'it' },
        { property: 'og:title', content: this.currentPost.fields.seoTitle.length >= 40 ? `${this.currentPost.fields.seoTitle} | Colorificio Postumia` : `${this.currentPost.fields.title} | Colorificio Postumia` },
        { property: 'og:description', content: this.currentPost.fields.seoDescription.length >= 100 ? this.currentPost.fields.seoDescription : `Non sai come ${this.currentPost.fields.title}? Inizia con i prodotti giusti! Gli esperti del Colorificio Postumia ti spiegano quali prodotti usare per ${this.currentPost.fields.title}` },
        { property: 'og:url', content: `/cosa-usare-per/${this.slug}` },
        { property: 'og:image', content: this.currentPost.fields.image.fields.file.url }
      ],
      link: [
        { rel: 'canonical', href: location.protocol + '//' + location.host + location.pathname }
      ]
    }
    return {
      title: 'Loading...',
      meta: [
        { vmid: 'description', name: 'description', content: 'Loading...' },
        { property: 'og:locale', content: 'Loading...' },
        { property: 'og:title', content: 'Loading...' },
        { property: 'og:description', content: 'Loading...' },
        { property: 'og:url', content: 'Loading...' }
      ],
      link: [
        { rel: 'alternate', href: 'Loading...', hreflang: 'it' }
      ]
    }
  }
}
</script>

<style lang="scss">
  @import "./content.scss";

  @import './../../assets/scss/breakpoint.scss';
  @import './../../assets/scss/typo.scss';
  
  .product-same-category{
    padding-top: 3rem;
  }

  .site-paddings{
    @include site-paddings;
  }

  .blog-post .title--section__title-area{
    border-top: 1px solid #E6E6E6;
    margin: 4rem 0;
    h3{
      position: absolute;
      left: 50%; top: 0;
      transform: translate3d(-50%, -50%, 0);
      text-transform: uppercase;
      letter-spacing: 2px;
      margin: 0;
      background-color: #FFFFEA;
      color: #484848;
      padding: 5px 15px;
      font-size: .9rem;
      width: 280px;
      text-align: center;
      @include media(">=large"){
        font-size: 1.0625rem;
        padding: 5px 35px;
        width: 340px;
      }
    }
  }

  .blog-post .swiper-container--product-same-category .swiper-wrapper .swiper-slide a{
    background-color: #FFFFEA;
  }
</style>
